import { Observable, observe, Scope } from 'src/3rds/cube'

// :: Types

export type BlocoTexto = {
    tipo: 'texto'
    texto: string
    /** Cartão de preview do link: indefinido = automático (liga quando há URL). */
    previewLink?: boolean
}

export type BlocoImagem = {
    tipo: 'imagem'
    assetId: number
    legenda?: string
    arquivo?: string
    mimeType?: string
    byteSize?: number
    /** URL local (objectURL) para exibir a imagem já enviada. */
    previewUrl?: string
}

export type Bloco = BlocoTexto | BlocoImagem

export type Campanha = {
    newsId: string
    ativa: boolean
    disparadaEm?: Date
    concluida: boolean
    concluidaEm?: Date
    conteudoAtualizadoEm?: Date
    criadaEm: Date
    enviados: number
    enfileirados: number
    totalBlocos: number
}

export type CampanhaDetalhe = Campanha & {
    blocos: Bloco[]
    testada: boolean
    testadaEm?: Date
    template: TemplateDaCampanha
}

export type SituacaoDoTemplate =
    | 'sem_template'
    | 'em_analise'
    | 'aprovado'
    | 'recusado'
    | 'pausado'
    | 'desatualizado'
    | 'nao_encontrado'

/** O template da campanha para quem está fora da janela de 24 h (espelha o servidor). */
export type TemplateDaCampanha = {
    situacao: SituacaoDoTemplate
    nome?: string
    idioma?: string
    criadoEm?: string
    motivoRecusa?: string
    /** Por que o conteúdo não cabe num template; vazio quando cabe. */
    problemas: string[]
    podeCriar: boolean
    /** A escolha da campanha: true = também quem está fora da janela, pelo template (pago); false = só a janela. */
    foraDaJanela: boolean
    /** Escolhido e aprovado: quem está fora da janela recebe o template. */
    enviando: boolean
    /** Por que o disparo ainda não pode sair, com o template escolhido mas não pronto. */
    impedimentoDoDisparo?: string
    /** Público do template: ausente = todos; N = quem atuou nos últimos N dias. */
    publicoDias?: number
}

/** O que a exclusão de uma campanha leva junto. */
export type PreviaDaExclusao = {
    newsId: string
    impedimento?: string
    entregas: number
    templates: { id: string; nome: string; idioma: string; status: string; excluivel: boolean; motivo?: string }[]
}

export type ResultadoDaExclusao = {
    newsId: string
    entregasExcluidas: number
    imagensExcluidas: number
    templatesExcluidos: string[]
    templatesNaoExcluidos: { nome: string; motivo: string }[]
}

export type Alcance = {
    parceirosTotal: number
    comPhone1: number
    janelaAberta: number
    alcancaveisAgora: number
    jaEnviados: number
    /** Fora da janela e sem ter recebido, no público da campanha: só o template os alcança. */
    foraDaJanela: number
    /** O mesmo total para cada público: todos (dias nulo) e cada período. */
    foraDaJanelaPorPublico: { dias: number | null; parceiros: number }[]
    /** Quem está fora da janela recebe o template se a campanha for disparada agora. */
    templateNoDisparo: boolean
}

export type Entrega = {
    ownerId: string
    nome?: string
    status: string
    enfileiradaEm?: Date
    enviadaEm?: Date
    ultimaInteracaoEm?: Date
}

export type Situacao = {
    newsId: string
    porStatus: Record<string, number>
    fila: Record<string, number>
    erros400: number
    entregas: Entrega[]
}

export type Parceiro = {
    ownerId: string
    nome: string
    email: string
    telefone?: string
}

export type Diagnostico = {
    encontrado: boolean
    ownerId: string
    nome?: string
    telefone?: string
    phone1Ok: boolean
    somenteEmPhone2: boolean
    duplicadoCom: { ownerId: string; nome: string }[]
    janelaAberta: boolean
    ultimaInteracaoEm?: Date
    jaRecebeu?: boolean
    podeReceberAgora: boolean
    impedimentos: string[]
    /** Fora da janela, mas recebe o template da campanha. */
    recebeTemplate?: boolean
}

export type Aba = 'editor' | 'disparo' | 'situacao'

// :: Scopes

@Observable
export class CampanhaScope extends Scope {
    @observe() aba: Aba = 'editor'

    // Lista
    @observe() campanhas: Campanha[] = []
    @observe() carregandoLista = false

    // Editor
    @observe() newsId = ''
    @observe() blocos: Bloco[] = []
    @observe() campanha?: CampanhaDetalhe
    @observe() salvando = false
    @observe() enviandoImagem = false
    @observe() erroFormulario?: string
    /** Há edição não salva na tela. */
    @observe() alterado = false

    // Disparo
    @observe() alcance?: Alcance
    @observe() carregandoAlcance = false
    @observe() parceiros: Parceiro[] = []
    @observe() parceiroTeste?: Parceiro
    @observe() carregandoParceiros = false
    @observe() diagnostico?: Diagnostico
    @observe() carregandoDiagnostico = false
    @observe() enviandoTeste = false
    /** Teste enfileirado, esperando a fila confirmar a entrega. */
    @observe() aguardandoTeste = false
    @observe() confirmacaoAberta = false
    @observe() disparando = false
    /** Só admin cria o template da campanha e liga o envio dele. */
    @observe() podeGerirTemplate = false
    // Exclusão
    @observe() exclusao?: PreviaDaExclusao
    @observe() excluirTemplates = false
    @observe() excluindo = false

    /** Criando o template ou mudando o envio dele. */
    @observe() salvandoTemplate = false

    // Situação
    @observe() situacao?: Situacao
    @observe() carregandoSituacao = false
    @observe() baixandoRelatorio = false

    onTrocarAba = Scope.ASYNC_ACTION_ONE<Aba>()
    onNovaCampanha = Scope.ASYNC_ACTION
    onAbrirCampanha = Scope.ASYNC_ACTION_ONE<string>()
    onNewsIdChange = Scope.ASYNC_ACTION_STRING
    onAdicionarTexto = Scope.ASYNC_ACTION
    onSelecionarImagem = Scope.ASYNC_ACTION_ONE<File>()
    onAlterarBloco = Scope.ASYNC_ACTION_ONE<{ indice: number; bloco: Bloco }>()
    onRemoverBloco = Scope.ASYNC_ACTION_ONE<number>()
    onMoverBloco = Scope.ASYNC_ACTION_ONE<{ indice: number; direcao: -1 | 1 }>()
    onSalvar = Scope.ASYNC_ACTION
    onBuscarParceiro = Scope.ASYNC_ACTION_STRING
    onSelecionarParceiro = Scope.ASYNC_ACTION_ONE<Parceiro | undefined>()
    onEnviarTeste = Scope.ASYNC_ACTION
    onAbrirConfirmacao = Scope.ASYNC_ACTION
    onFecharConfirmacao = Scope.ASYNC_ACTION
    onDisparar = Scope.ASYNC_ACTION
    /** Público do template: '' = todos; '30', '60'... = quem atuou nesse período. */
    onPublicoChange = Scope.ASYNC_ACTION_STRING
    onAbrirExclusao = Scope.ASYNC_ACTION_ONE<string>()
    onFecharExclusao = Scope.ASYNC_ACTION
    onExcluirTemplatesChange = Scope.ASYNC_ACTION_ONE<boolean>()
    onConfirmarExclusao = Scope.ASYNC_ACTION
    onCriarTemplate = Scope.ASYNC_ACTION
    onTemplateForaDaJanela = Scope.ASYNC_ACTION_ONE<boolean>()
    onAtualizarTemplate = Scope.ASYNC_ACTION
    onPausar = Scope.ASYNC_ACTION
    onRetomar = Scope.ASYNC_ACTION
    onAtualizarSituacao = Scope.ASYNC_ACTION
    onBaixarRelatorio = Scope.ASYNC_ACTION
}
